import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { invalidateEventRegistrationResponsesFilter } from './useEventRegistrationResponsesFilter';
import { invalidateEventRegistrationStats } from './useEventRegistrationStats';

/**
 * Wire values for the API's OfflinePaymentMethod enum. These are the enum member names the API
 * binds and serializes; any other casing is rejected by the request binder.
 */
export type OfflinePaymentMethod = 'Cash' | 'Check' | 'CreditCardOffline' | 'Comp' | 'Other';

export interface IManualRegistrationRequest {
	contactId?: string;
	newContact?: {
		firstName: string;
		lastName: string;
		email: string;
	};
	isAttending: boolean;
	attendeeTicketId?: string;
	/** Which physical ticket (table) the attendee sits on, for multi-seat tickets; omit for single-seat. */
	attendeeTicketItemIndex?: number | null;
	guests?: {
		ticketId?: string;
		firstName: string;
		lastName: string;
		email?: string;
		/** Which physical ticket (table) this guest sits on, for multi-seat tickets; omit for single-seat. */
		ticketItemIndex?: number | null;
	}[];
	payment?: {
		markAsPaid: boolean;
		method?: OfflinePaymentMethod;
		amount?: number;
	};
	customProperties?: Record<string, string>;
}

export interface IManualRegistrationResponse {
	responseId: string;
	confirmationNumber: string;
	contactId: string;
}

export const useManualRegistrationMutation = ({
	impersonationContext,
	onError,
	onSuccess,
}: {
	impersonationContext?: Api.IImpersonationContext;
	onError?: (error: Api.IOperationResultNoValue) => void;
	onSuccess?: (data?: IManualRegistrationResponse) => void;
}) => {
	const userSession = useUserSession();

	return useMutation({
		mutationFn: ({ surveyId, request }: { surveyId: string; request: IManualRegistrationRequest }) => {
			return userSession.webServiceHelper.callAsync<IManualRegistrationResponse>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					urlPath: `survey/eventRegistration/${surveyId}/manualRegistration`,
				}),
				'POST',
				request
			);
		},
		onSuccess: (data, variables) => {
			invalidateEventRegistrationResponsesFilter();
			invalidateEventRegistrationStats(variables.surveyId);
			onSuccess?.(data);
		},
		onError,
	});
};
